import { Button } from "@/components/ui/button";
import { useDocumentos } from "@/hooks/useDocumentos";
import { Skeleton } from "@/components/ui/Skeleton";
import { Users, Calendar, FileText } from "lucide-react";

import type { ProgramaClienteFinal } from "@/interfaces/programa-cliente-final.interface";

export type ProgramaCardGestionProps = {
  programa: ProgramaClienteFinal;
  onViewDetails?: (programa: ProgramaClienteFinal) => void;
  onEdit?: (programa: ProgramaClienteFinal) => void;
};

export function ProgramaCardGestion({
  programa,
  onViewDetails,
  onEdit,
}: ProgramaCardGestionProps) {
  const { data: documentosData, isLoading: isLoadingDocumentos } =
    useDocumentos({
      idPrograma: programa.id,
    });

  if (isLoadingDocumentos) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-6 w-80" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const lugaresDisponibles =
    programa.cupoMaximo > 0
      ? programa.cupoMaximo - (programa.learnersCount ?? 0)
      : null;
  const archivosCount = documentosData?.data?.length ?? 0;

  return (
    <div className="border rounded-lg p-5 bg-[var(--background)] shadow-md hover:shadow-lg transition-shadow flex flex-col justify-between h-full">
      {/* Header con nombre y estado */}
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-xl font-bold text-[var(--foreground)] pr-2 line-clamp-2">
          {programa.nombre}
        </h3>
        <span
          className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
            programa.isActivo
              ? "bg-green-500/10 text-green-600 dark:bg-green-500/20 dark:text-green-400"
              : "bg-red-500/10 text-red-600 dark:bg-red-500/20 dark:text-red-400"
          }`}
        >
          {programa.isActivo ? "Activo" : "Inactivo"}
        </span>
      </div>

      {/* Métricas principales */}
      <div className="space-y-3 mb-4">
        {/* Aprendices */}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
            <Users className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">Aprendices</p>
            <p className="text-sm font-semibold">
              {programa.learnersCount ?? 0}
              <span className="text-xs font-normal text-muted-foreground ml-2">
                / {programa.cupoMaximo ?? 0} ({lugaresDisponibles ?? 0}{" "}
                disponibles)
              </span>
            </p>
          </div>
        </div>

        {/* Periodo */}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/10 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400">
            <Calendar className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">Periodo</p>
            <p className="text-sm font-semibold">
              {new Date(programa.fechaInicio).toLocaleDateString("es-ES", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}{" "}
              -{" "}
              {new Date(programa.fechaFin).toLocaleDateString("es-ES", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </p>
          </div>
        </div>

        {/* Archivos */}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-500/10 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400">
            <FileText className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">Base de conocimiento</p>
            <p className="text-sm font-semibold">
              {archivosCount} {archivosCount === 1 ? "archivo" : "archivos"}
            </p>
          </div>
        </div>

        {/* Tono */}
        {/* <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-500/10 text-green-600 dark:bg-green-500/20 dark:text-green-400">
            <TrendingUp className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">Tono</p>
            <p className="text-sm font-semibold">{programa.tono}</p>
          </div>
        </div> */}
      </div>

      {/* Acciones */}
      <div className="pt-4 border-t flex flex-col gap-2 sm:flex-row">
        {onViewDetails ? (
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onViewDetails(programa)}
          >
            Ver detalles
          </Button>
        ) : null}
        {onEdit ? (
          <Button className="flex-1" onClick={() => onEdit(programa)}>
            Editar
          </Button>
        ) : null}
      </div>
    </div>
  );
}
